import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, ShoppingCart, Heart, LogOut, LayoutDashboard } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { SearchBar } from "./SearchBar";
import { Button } from "./ui/button";
import { useAuthStore } from "@/store/authStore";
import { useCartStore } from "@/store/cartStore";
import { CATEGORY_NAV_ITEMS } from "@/types";
import { cn } from "@/lib/utils";

function navHref(value: string) {
  if (value === "NEW_IN") return "/shop?isNew=true";
  if (value === "MADE_IN_CHINA") return "/shop?madeInChina=true";
  if (value === "DISCOVER") return "/shop";
  return `/shop?category=${value}`;
}

const NAV_ITEMS = CATEGORY_NAV_ITEMS.filter(
  item => item.value !== "MADE_IN_CHINA" && item.value !== "PAINTS_AND_CHEMICALS"
);

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const itemCount = useCartStore((s) => s.itemCount());
  const openCartDrawer = useCartStore((s) => s.openDrawer);
  const navigate = useNavigate();

  return (
    <>
      {/* ── Top contact row (desktop only) ──────── */}
      <div className="hidden bg-steel text-white/85 lg:block">
        <div className="container-page flex h-9 items-center justify-between text-[11px]">
          <div className="flex items-center gap-4">
            <span className="font-medium text-white">Call to Order</span>
            <a
              href="https://wa.me/254722758756"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-green-400 transition-colors"
              title="Chat on WhatsApp"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400">
                <path d="M17.498 14.382c-.3-.15-1.77-.875-2.044-.975-.274-.1-.474-.15-.674.15-.2.3-.774.975-.95 1.175-.174.2-.35.225-.65.075-.3-.15-1.267-.467-2.417-1.492-.894-.797-1.498-1.78-1.673-2.08-.174-.3-.018-.463.132-.614.134-.134.3-.35.45-.524.15-.175.2-.3.3-.5.1-.2.05-.375-.025-.525-.075-.15-.674-1.625-.924-2.225-.243-.58-.49-.5-.674-.51-.174-.008-.374-.01-.574-.01-.2 0-.524.075-.8.374-.274.3-1.048 1.024-1.048 2.495 0 1.472 1.072 2.895 1.222 3.095.15.2 2.11 3.22 5.112 4.517.714.308 1.27.492 1.704.63.717.228 1.37.196 1.887.119.576-.085 1.77-.724 2.02-1.423.25-.7.25-1.3.175-1.423-.075-.125-.274-.2-.574-.35z" />
              </svg>
              WhatsApp
            </a>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:+254722758756" className="hover:text-white transition-colors">
              +254 722758756
            </a>
            <span className="text-white/30">|</span>
            <a href="tel:+254727102811" className="hover:text-white transition-colors">
              +254 727102811
            </a>
            <span className="text-white/30">|</span>
            <span>Baba Dogo Junction Spectrum Business Park Godown 18</span>
          </div>
        </div>
      </div>

      {/* ── Main header ───────────────────────────── */}
      <div className="bg-white border-b border-border">
        <div className="container-page flex h-16 sm:h-20 items-center gap-3 sm:gap-6">
          {/* Hamburger (mobile only) */}
          <button
            className="lg:hidden shrink-0"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6 text-steel" /> : <Menu className="h-6 w-6 text-steel" />}
          </button>

          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="August Auto Agencies Home">
            <img
              src="/logo.png"
              alt="August Auto Agencies"
              className="h-10 sm:h-20 w-auto object-contain"
            />
            
          </Link>

          <div className="hidden flex-1 md:block">
            <SearchBar />
          </div>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-4">
            <Link to="/account/wishlist" aria-label="Wishlist" className="hidden sm:block">
              <Heart className="h-6 w-6 text-steel" />
            </Link>

            <button onClick={openCartDrawer} aria-label={`Cart, ${itemCount} items`} className="relative">
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-steel" />
              {itemCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 font-mono text-[11px] font-bold text-white">
                  {itemCount}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button className="flex items-center gap-2" aria-label="Account menu">
                    <User className="h-5 w-5 sm:h-6 sm:w-6 text-steel" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="end"
                    sideOffset={8}
                    className="z-50 w-56 rounded-sm border border-border bg-white p-1.5 shadow-plate"
                  >
                    <p className="px-3 py-2 text-sm font-medium text-steel truncate">{user.fullName}</p>
                    <DropdownMenu.Separator className="my-1 h-px bg-border" />
                    <DropdownMenu.Item asChild>
                      <Link
                        to="/account/orders"
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm text-steel hover:bg-concrete-100 focus-visible:outline-none"
                      >
                        My Orders
                      </Link>
                    </DropdownMenu.Item>
                    {user.role === "AGENT" && (
                      <DropdownMenu.Item asChild>
                        <Link
                          to="/dashboard"
                          className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm text-steel hover:bg-concrete-100 focus-visible:outline-none"
                        >
                          <LayoutDashboard className="h-4 w-4" /> Agent Dashboard
                        </Link>
                      </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item
                      onSelect={async () => {
                        await logout();
                        navigate("/");
                      }}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm text-rust hover:bg-rust/10 focus-visible:outline-none"
                    >
                      <LogOut className="h-4 w-4" /> Log Out
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                <Button variant="ghost" size="sm" asChild className="text-xs sm:text-sm px-2 sm:px-4">
                  <Link to="/account/login">Log In</Link>
                </Button>
                <Button variant="amber" size="sm" asChild className="hidden sm:flex bg-red-600 text-white hover:bg-red-700 text-xs">
                  <Link to="/account/register">Sign Up</Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile search */}
        <div className="px-4 pb-3 md:hidden">
          <SearchBar />
        </div>
      </div>

      {/* ── Desktop category navigation (sticky) ──── */}
      <nav
        className="sticky top-0 z-30 hidden border-b border-border bg-white lg:block"
        aria-label="Product categories"
      >
        <div className="container-page flex h-[58px] items-center justify-between overflow-hidden">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.value}
              to={navHref(item.value)}
              className="whitespace-nowrap font-display text-[11px] font-bold uppercase tracking-wider text-steel transition-colors hover:bg-red-600 hover:text-white px-2 py-1 rounded-sm"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </nav>

      {/* ── Mobile category menu ── */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-white shadow-lg">
          <nav aria-label="Product categories" className="container-page flex flex-col py-3">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.value}
                to={navHref(item.value)}
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-sm px-4 py-3 font-display text-sm font-medium uppercase tracking-wide text-steel hover:bg-concrete-100"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}